import React, {CSSProperties, ReactNode} from 'react'

import ListItem from '../surfaces/ListItem.js'
import P from '../typography/P.js'
import {Check, RadioButton} from '../forms/index.js'
import {popupStyle} from './styles.js'

export interface PopupOption {
    title?: string
    value?: any
    subvalue?: any
    /** Multiple: whether it is checked (written into the option object when clicked) */
    selected?: boolean
    disabled?: boolean
    /** false: the title wraps instead of ending in ... */
    ellipsis?: boolean
    /** Style of the title (and the ListItem, which gets all props of the option) */
    style?: CSSProperties
    /** Shown at the right of the title */
    append?: ReactNode
    [prop: string]: any
}

interface PopupOptionsProps {
    /** An option with a string title and value is a ListItem, anything else (an element) is rendered as it is */
    options: (PopupOption | ReactNode)[]
    multiple?: boolean
    /** A radio button (a checkbox when multiple) in front of every option */
    showOptionRadioButton?: boolean
    onClickOption: (option: PopupOption, index: number) => void
}

const isListOption = (option: PopupOption | ReactNode): option is PopupOption => typeof (option as PopupOption)?.title === 'string' && typeof (option as PopupOption)?.value === 'string'

/** The list of openOptions: clicking one chooses it, or toggles it when multiple */
export default function PopupOptions({options, multiple, showOptionRadioButton, onClickOption}: PopupOptionsProps) {
    return (
        <div style={{maxHeight: popupStyle.optionsMaxHeight, overflowY: 'auto'}}>
            {options.map((option, index) => {
                if (!isListOption(option)) {
                    return option
                }

                return (
                    <ListItem
                        key={`${option.value}${option.subvalue || ''}`}
                        disabled={option.disabled}
                        onClick={option.disabled ? () => {} : () => onClickOption(option, index)}
                        {...option}
                    >
                        {showOptionRadioButton && (multiple ?
                            <Check disabled={option.disabled} checked={option.selected} /> :
                            <RadioButton disabled={option.disabled} checked={option.selected} />
                        )}

                        {/* option.style wins over the ellipsis (the call sites only set a colour in it) */}
                        <P ellipsis={option.ellipsis !== false} style={{flex: 1, ...option.style}} disabled={option.disabled}>{option.title}</P>

                        {option.append &&
                            <div style={{marginLeft: 6}}>{option.append}</div>
                        }
                    </ListItem>
                )
            })}
        </div>
    )
}
